import { fixturesState } from '../fixtures';
import { FEATURE_NOTIFICACION_CORREO_HABILITADA } from '../featureFlags';

// Extend window type for development functions
declare global {
  interface Window {
    resetearFixturesCups?: () => void;
  }
}

interface ProgresoGestion {
  PorcentajeAprobados: number;
  PorcentajePendientes: number;
  PorcentajeRechazados: number;
  TotalAprobados: number;
  TotalCups: number;
  TotalPendientes: number;
  TotalRechazados: number;
}

interface Servicio {
  CantidadAprobados: number;
  CantidadCups: number;
  CantidadPendientes: number;
  CantidadRechazados: number;
  Encabezado: string | null;
  Id: number;
  Observaciones: string | null;
  Servicio: string;
}

interface AcuerdosTarifariosServiciosResponse {
  ResponseCode: number;
  ResponseDetail: string;
  Result: {
    __type: string;
    ProgresoGestion: ProgresoGestion;
    Servicios: Servicio[];
  };
}

interface EstadoObjeto {
  IsSuccess: boolean;
  Message: string;
  ObjectId: number;
}

interface EstadoObjetosResponse {
  ResponseCode: number;
  ResponseDetail: string;
  Result: {
    __type: string;
    EstadoObjetos: EstadoObjeto[];
    IdsError: number;
    IdsOk: number;
  };
}

interface ActualizarEstadoAnexoResponse {
  ResponseCode: number;
  ResponseDetail: string;
  Result: {
    __type: string;
    Code: number;
    Description: string;
    Result: string;
    EstadoNotificacion?: string;
    MensajeNotificacion?: string;
    CorreoDestino?: string;
  };
}

class CupsService {
  private isDevelopment = __DEV__ || import.meta.env.MODE === 'development';
  private readonly TIMEOUT_DEFAULT_MS = 60_000;
  // CUPS_SERVICIOS es O(n) en OnBase — 4k+ CUPS pueden tardar varios minutos
  private readonly TIMEOUT_CUPS_SERVICIOS_MS = 300_000;
  private readonly CUPS_PAGINATION_SIZE = (() => {
    const envValue = Number(import.meta.env.VITE_ANEXOS_CUPS_PAGE_SIZE || 800);
    if (Number.isFinite(envValue) && envValue > 0) {
      return Math.floor(envValue);
    }
    return 800;
  })();

  private fetchWithTimeout(url: string, options: RequestInit, timeoutMs: number): Promise<Response> {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);
    return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(id));
  }

  async obtenerServiciosTarifarios(anexoId: number): Promise<AcuerdosTarifariosServiciosResponse> {
    if (this.isDevelopment) {
      console.log('🔄 [Dev] Obteniendo servicios desde fixtures dinámicos');
      
      // Simular delay de red
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return fixturesState.getServiciosData();
    } else {
      console.log('🌐 [Prod] Obteniendo servicios desde API');
      
      const response = await this.fetchWithTimeout(
        `/_vti_bin/Servicios/ServicioTransar.svc/ObtenerServiciosTarifarios?anexoId=${anexoId}&pagination=${this.CUPS_PAGINATION_SIZE}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        },
        this.TIMEOUT_CUPS_SERVICIOS_MS
      );

      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      return await response.json();
    }
  }

  async actualizarEstadoCups(
    objectIds: number[],
    tipo: 'CUPS' | 'CUPS_SERVICIOS',
    estado: 'SI' | 'NO',
    observaciones?: string
  ): Promise<EstadoObjetosResponse> {
    if (this.isDevelopment) {
      console.log('🔄 [Dev] Simulando actualización de estado:', { tipo, objectIds, estado });

      // Simular delay de red
      await new Promise(resolve => setTimeout(resolve, 1500));

      const nuevoEstado = estado === 'SI' ? 'Aprobado' : 'Rechazado';

      if (tipo === 'CUPS') {
        fixturesState.actualizarEstadoCups(objectIds, nuevoEstado);
      } else {
        fixturesState.actualizarEstadoServicio(objectIds, nuevoEstado);
      }

      return {
        ResponseCode: 200,
        ResponseDetail: 'OK',
        Result: {
          __type: 'EstadoObjetosResponse',
          IdsOk: objectIds.length,
          IdsError: 0,
          EstadoObjetos: objectIds.map(id => ({
            IsSuccess: true,
            Message: `${tipo} ${estado === 'SI' ? 'aprobado' : 'rechazado'} correctamente`,
            ObjectId: id
          }))
        }
      };
    }

    console.log('🌐 [Prod] Actualizando estado:', { tipo, objectIds, estado });

    const tipoNormalizado = (tipo || '').trim().toUpperCase();
    if (tipoNormalizado !== 'CUPS' && tipoNormalizado !== 'CUPS_SERVICIOS') {
      throw new Error(`Tipo inválido para actualización de CUPS: ${tipo}`);
    }

    const objectIdsNormalizados = (objectIds || [])
      .filter(id => Number.isFinite(id) && id > 0)
      .map(id => Math.trunc(id));

    if (objectIdsNormalizados.length === 0) {
      throw new Error('No hay ObjectIds válidos para actualizar');
    }

    // Obtener información del aprobador del localStorage para trazabilidad operativa
    const aprobadorInfo = localStorage.getItem('aprobador-info');
    let aprobador = null;

    if (aprobadorInfo) {
      try {
        aprobador = JSON.parse(aprobadorInfo);
      } catch (e) {
        console.error('Error al parsear aprobador-info:', e);
      }
    }

    interface RequestBody {
      Tipo: string;
      ObjectIds: number[];
      Estado: string;
      Observaciones?: string;
      NombreAprobador?: string;
      CorreoAprobador?: string;
      IpAprobador?: string;
    }

    const body: RequestBody = {
      Tipo: tipoNormalizado,
      ObjectIds: objectIdsNormalizados,
      Estado: estado,
      NombreAprobador: aprobador?.nombre || '',
      CorreoAprobador: aprobador?.correo || '',
      IpAprobador: aprobador?.ip || ''
    };

    if (observaciones) {
      body.Observaciones = observaciones;
    }

    // CUPS_SERVICIOS actualiza cada CUPS individualmente en OnBase — timeout extendido para volúmenes altos
    const timeoutMs = tipoNormalizado === 'CUPS_SERVICIOS' ? this.TIMEOUT_CUPS_SERVICIOS_MS : this.TIMEOUT_DEFAULT_MS;

    const response = await this.fetchWithTimeout(
      '/_vti_bin/Servicios/ServicioTransar.svc/ActualizarEstadoCupsTarifario',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      },
      timeoutMs
    );

    if (!response.ok) {
      throw new Error(`Error HTTP: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  verificarCupsPendientes(): number {
    if (this.isDevelopment) {
      const cups = fixturesState.getCupsData().Result.Cups;
      return cups.filter(cup => cup.Estado === 'Pendiente').length;
    } else {
      // En producción, esta verificación se hace usando los totales del modal
      // que se pasan como parámetro en el componente
      throw new Error('Use verificarCupsPendientesConTotales para modo producción');
    }
  }

  verificarCupsPendientesConTotales(totalPendientes: number): number {
    return totalPendientes;
  }

  resetearFixtures(): void {
    if (this.isDevelopment) {
      fixturesState.reset();
      console.log('🔄 Fixtures reseteados al estado inicial');
    } else {
      console.warn('Esta función solo está disponible en modo desarrollo');
    }
  }

  async notificarGestionCompleta(
    anexoId: number, 
    totalCups: number, 
    totalRechazados: number,
    nombreAnexoCorreo?: string,
    codigoAnexoCorreo?: string,
    onMostrarResultado?: (
      tipo: 'aprobado' | 'rechazado', 
      porcentaje: number, 
      totalRechazados: number,
      correoDestino?: string,
      estadoNotificacion?: string,
      mensajeNotificacion?: string
    ) => void,
    correoOverride?: string
  ): Promise<void> {
    // Calcular porcentaje de rechazos
    const porcentajeRechazados = totalCups > 0 ? (totalRechazados / totalCups) * 100 : 0;
    const tipoResultado: 'aprobado' | 'rechazado' = porcentajeRechazados > 10 ? 'rechazado' : 'aprobado';
    
    console.log('📊 [Análisis] Totales:', { totalCups, totalRechazados, porcentajeRechazados: porcentajeRechazados.toFixed(2) + '%', tipoResultado });
    
    if (this.isDevelopment) {
      console.log('🔔 [Dev] Simulando notificación de gestión completa para anexo:', anexoId);
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('✅ [Dev] Notificación simulada enviada correctamente');
      
      let correoSimulado = correoOverride;
      if (!correoSimulado && typeof localStorage !== 'undefined') {
        try {
          const info = JSON.parse(localStorage.getItem('aprobador-info') || '{}');
          correoSimulado = info?.correo;
        } catch (e) {
          // ignore
        }
      }

      if (onMostrarResultado) {
        onMostrarResultado(
          tipoResultado, 
          porcentajeRechazados, 
          totalRechazados,
          correoSimulado || 'prestador@compensar.com',
          'EXITOSA',
          'Notificación enviada correctamente.'
        );
      }
    } else {
      console.log('🔔 [Prod] Enviando notificación de gestión completa para anexo:', anexoId);
      
      try {
        const respuesta = await this.actualizarEstadoAnexo(
          anexoId, 
          "0", 
          undefined, 
          tipoResultado, 
          nombreAnexoCorreo, 
          codigoAnexoCorreo, 
          correoOverride
        ) as ActualizarEstadoAnexoResponse;
        
        console.log('✅ [Prod] Estado del anexo actualizado a gestión completa:', { anexoId, estado: '0', tipoResultado });

        const correoFinal = respuesta?.Result?.CorreoDestino || correoOverride || '';
        const estadoNotif = respuesta?.Result?.EstadoNotificacion;
        const mensajeNotif = respuesta?.Result?.MensajeNotificacion;

        if (onMostrarResultado) {
          onMostrarResultado(
            tipoResultado, 
            porcentajeRechazados, 
            totalRechazados,
            correoFinal,
            estadoNotif,
            mensajeNotif
          );
        }
      } catch (error) {
        console.error('❌ [Prod] Error al actualizar estado del anexo:', error);
        throw error;
      }
    }
  }

  async actualizarEstadoAnexo(
    anexoId: number,
    estado: "0" | "5",
    observaciones?: string,
    resultadoGestionCorreo?: 'aprobado' | 'rechazado',
    nombreAnexoCorreo?: string,
    codigoAnexoCorreo?: string,
    correoOverride?: string
  ): Promise<ActualizarEstadoAnexoResponse | void> {
    if (this.isDevelopment) {
      console.log('🔄 [Dev] Simulando actualización de estado del anexo:', {
        anexoId,
        estado,
        observaciones,
        resultadoGestionCorreo,
        nombreAnexoCorreo,
        codigoAnexoCorreo,
        correoOverride
      });
      await new Promise(resolve => setTimeout(resolve, 300));
      return;
    }

    console.log('🔄 [Prod] Actualizando estado del anexo:', { anexoId, estado });

    try {
      // Obtener información del aprobador del localStorage
      const aprobadorInfo = localStorage.getItem('aprobador-info');
      
      if (!aprobadorInfo) {
        throw new Error('No se encontró información del aprobador');
      }
      
      const aprobador = JSON.parse(aprobadorInfo);
      const correoAprobadorFinal = (correoOverride || aprobador.correo || '').trim();

      const body: {
        AnexoId: number;
        Estado: string;
        NombreAprobador: string;
        CorreoAprobador: string;
        IpAprobador: string;
        EstadoCups: string;
        Observaciones?: string;
        ResultadoGestionCorreo?: string;
        NombreAnexoCorreo?: string;
        CodigoAnexoCorreo?: string;
        TestMode?: boolean;
        EnviarNotificacionCorreo?: boolean;
      } = {
        AnexoId: anexoId,
        Estado: estado,
        NombreAprobador: aprobador.nombre,
        CorreoAprobador: correoAprobadorFinal,
        IpAprobador: aprobador.ip,
        EstadoCups: estado === "0" ? "" : "SI",
        TestMode: false,
        EnviarNotificacionCorreo: FEATURE_NOTIFICACION_CORREO_HABILITADA
      };


      // Solo incluir observaciones si se proporcionan
      if (observaciones) {
        body.Observaciones = observaciones;
      }

      // Campo adicional para notificación por email; no altera Estado ni EstadoCups legacy.
      if (resultadoGestionCorreo) {
        body.ResultadoGestionCorreo = resultadoGestionCorreo;
      }

      if (nombreAnexoCorreo) {
        body.NombreAnexoCorreo = nombreAnexoCorreo;
      }

      body.CodigoAnexoCorreo = codigoAnexoCorreo || anexoId.toString();

      const response = await this.fetchWithTimeout(
        '/_vti_bin/Servicios/ServicioTransar.svc/ActualizarEstadoAnexotarifario',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        },
        this.TIMEOUT_DEFAULT_MS
      );
      
      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const data: ActualizarEstadoAnexoResponse = await response.json();
      
      if (data.ResponseCode !== 200 || !data.Result || data.Result.Result !== "OK") {
        throw new Error(data.ResponseDetail || 'Error al actualizar estado del anexo');
      }

      console.log('✅ [Prod] Estado del anexo actualizado exitosamente');
      return data;
    } catch (error) {
      console.error('❌ [Prod] Error al actualizar estado del anexo:', error);
      throw error;
    }
  }


  esDesarrollo(): boolean {
    return this.isDevelopment;
  }
}

// Exportar una instancia singleton
export const cupsService = new CupsService();

// Exponer función de reset en desarrollo
if (cupsService.esDesarrollo()) {
  window.resetearFixturesCups = () => cupsService.resetearFixtures();
  console.log('🛠️ [Dev] Función disponible: window.resetearFixturesCups()');
}